/* restock.js — Sneak Freak (AphCom) · "Notify me" back-in-stock alerts for sold-out variants (Figma C2 · C3 · C5)
   <sf-restock-sheet> (snippets/restock-sheet.liquid, rendered by snippets/buy-buttons.liquid on the product page)
   drives three things:
     · the sold-out buy state inside [data-buy-buttons]: the button label, the "On the list" state and the notes.
       product-form.js swaps [data-buy-buttons] for the new variant's copy and THEN dispatches `variant:change`, so
       every size or colour change re-reads [data-restock-data] and re-applies the state;
     · the sheet: form → done, manage. Email goes to the STOQ back-in-stock app (one POST per chosen size to its
       public intents endpoint, no key); STOQ keeps the request, matches the exact variant, sends one email when it
       is back and carries the unsubscribe link. WhatsApp is a JSON POST to the merchant's endpoint and exists only
       when the settings name one. Done is shown only after the service answered that it has the request.
   Device list: localStorage 'sf-restock-alerts' = [{v, p, size, ch, to, t}], written only after STOQ (or the
   endpoint) confirmed. It drives "On the list", Manage and duplicate detection; STOQ's waitlist is the record.
   Requires theme.js (sf-drawer). No dependencies. */
'use strict';
(function () {
  if (customElements.get('sf-restock-sheet')) return;
  const DEVICE_KEY = 'sf-restock-alerts';
  const DRAFT_KEY = 'sf-restock-draft';
  const RATE_KEY = 'sf-restock-rate';
  const STOQ_URL = 'https://app.stoqapp.com/api/v1/intents.json';
  const SEND_MS = 10000;
  /* sizes one tab may send in RATE_MS: the endpoint takes any address, so the sheet keeps bursts out */
  const RATE_MAX = 6;
  const RATE_MS = 10 * 60 * 1000;
  const MAX_PICK = 2;
  const UNDO_MS = 5000;
  const EMAIL_RE = /^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)*\.[^\s@.]{2,}$/;
  /* Lebanese mobile numbers after +961: 3 + 6 digits, or 70/71/76/78/79/81 + 6 digits */
  const LB_MOBILE_RE = /^(?:3\d{6}|7[016789]\d{6}|81\d{6})$/;

  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const fill = (tpl, map) => String(tpl || '').replace(/\[(\w+)\]/g, (m, k) => (Object.prototype.hasOwnProperty.call(map, k) ? String(map[k]) : m));
  const read = (store, key, fallback) => {
    try { const raw = window[store].getItem(key); return raw == null ? fallback : JSON.parse(raw); } catch (e) { return fallback; }
  };
  const write = (store, key, val) => {
    try { if (val == null) window[store].removeItem(key); else window[store].setItem(key, JSON.stringify(val)); return true; } catch (e) { return false; }
  };
  const alertsAll = () => { const l = read('localStorage', DEVICE_KEY, []); return Array.isArray(l) ? l.filter((a) => a && a.v && a.ch) : []; };
  const saveAlerts = (list) => write('localStorage', DEVICE_KEY, list);
  const same = (a, b) => Number(a) === Number(b);
  /* on an Arabic page a size or an address inside a sentence is bidi-isolated, so "42 1/2 EU" is not printed
     as "EU 1/2 42" (the chips get the same treatment from unicode-bidi: plaintext) */
  const RTL = document.documentElement.dir === 'rtl';
  const iso = (s) => (RTL && s ? '\u2068' + s + '\u2069' : s);

  /** "+961 71 123 456" from what was typed, or null when it is not a Lebanese mobile number */
  function normalPhone(raw) {
    let d = String(raw || '').replace(/\D/g, '');
    if (d.indexOf('00961') === 0) d = d.slice(5);
    else if (d.indexOf('961') === 0 && d.length > 8) d = d.slice(3);
    if (d.charAt(0) === '0') d = d.slice(1);
    return LB_MOBILE_RE.test(d) ? '+961' + d : null;
  }
  function prettyPhone(e164) {
    const d = String(e164 || '').replace(/^\+961/, '');
    return '+961 ' + (d.length === 7 ? d.slice(0, 1) + ' ' + d.slice(1, 4) + ' ' + d.slice(4) : d.slice(0, 2) + ' ' + d.slice(2, 5) + ' ' + d.slice(5));
  }
  /** what the device keeps: enough to recognise the alert, never the full contact */
  function mask(ch, to) {
    if (ch === 'whatsapp') { const d = String(to || '').replace(/\D/g, ''); return '+961 •• ••• ' + d.slice(-3); }
    const at = String(to || '').split('@');
    const local = at[0] || '';
    return (local.length > 10 ? local.slice(0, 9) + '…' : local) + '@…';
  }

  class SFRestockSheet extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      try { this.i18n = JSON.parse(this.dataset.i18n || '{}'); } catch (e) { this.i18n = {}; }
      /* Shopify's t filter HTML-escapes its output ("We&#39;ll"), and these strings are written as text (textContent)
         or through esc() into markup, so decode them once here; otherwise the shopper reads a literal &#39;. */
      const dec = document.createElement('textarea');
      Object.keys(this.i18n).forEach((k) => { if (typeof this.i18n[k] === 'string') { dec.innerHTML = this.i18n[k]; this.i18n[k] = dec.value; } });
      this.drawer = this.closest('sf-drawer');
      this.sectionId = this.dataset.section;
      this.root = document.getElementById('shopify-section-' + this.sectionId) || (this.drawer && this.drawer.closest('[data-section-id], .pdp')) || document;
      this.waOn = this.dataset.wa === 'true' && !!this.dataset.waEndpoint;
      this.endpoint = this.dataset.waEndpoint || '';
      this.shop = this.dataset.shop || '';
      this.productId = Number(this.dataset.productId) || 0;
      this.heading = this.drawer && this.drawer.querySelector('[data-restock-heading]');
      this.emailForm = this.querySelector('[data-restock-email-form]');
      this.waForm = this.querySelector('[data-restock-wa-form]');
      /* the shopper tapped a size on this page: from then on a sold-out size is "that size", not "any size" */
      this.sizeChosen = false;
      this.changing = false;
      this.state = { h: null, pdp: null, chosen: [], ch: this.waOn ? 'whatsapp' : 'email', email: '', phone: '', replace: null, view: 'form' };
      this._ctrl = new AbortController();
      this.bind(this._ctrl.signal);
      this.restoreDraft();
      this.sync();
      this.handleHash();
    }

    disconnectedCallback() {
      if (this._ctrl) this._ctrl.abort();
      this._init = false;
    }

    /* ------------------------------------------------------------ wiring */
    bind(signal) {
      /* openers live inside [data-buy-buttons], which is replaced on every variant change: delegate from the section */
      this.root.addEventListener('click', (e) => {
        const b = e.target.closest('[data-restock-open], [data-restock-manage]');
        if (!b || (this.drawer && this.drawer.contains(b))) return;
        e.preventDefault();
        this.openFrom(b, b.hasAttribute('data-restock-manage') ? { view: 'manage' } : {});
      }, { signal });
      this.root.addEventListener('change', (e) => {
        if (e.target.matches && e.target.matches('[data-size-option] input[type="radio"]')) this.sizeChosen = true;
      }, { capture: true, signal });
      this.root.addEventListener('variant:change', () => this.sync(), { signal });

      this.addEventListener('click', (e) => this.onClick(e), { signal });
      this.addEventListener('change', (e) => this.onChipChange(e), { signal });
      this.addEventListener('keydown', (e) => this.onSegKey(e), { signal });
      this.addEventListener('input', (e) => {
        const t = e.target;
        if (t.matches('[data-restock-email], [data-restock-phone]')) {
          this.clearFieldError(t);
          if (t.matches('[data-restock-email]')) this.state.email = t.value.trim(); else this.state.phone = t.value;
          clearTimeout(this._draftTimer);
          this._draftTimer = setTimeout(() => this.saveDraft(), 250);
        }
      }, { signal });
      if (this.emailForm) this.emailForm.addEventListener('submit', (e) => this.onEmailSubmit(e), { signal });
      if (this.waForm) this.waForm.addEventListener('submit', (e) => this.onWaSubmit(e), { signal });
      if (this.drawer) this.drawer.addEventListener('drawer:close', () => this.onClosed(), { signal });
      window.addEventListener('pageshow', () => this.loading(false), { signal });
    }

    /* ------------------------------------------------------------ the selected variant */
    readData() {
      const node = this.root.querySelector('[data-buy-buttons] [data-restock-data]');
      if (!node) return null;
      if (node === this._dataNode) return this._data;
      let d = null;
      try { d = JSON.parse(node.textContent); } catch (e) { d = null; }
      if (d && d.variant) {
        d.variant.id = Number(d.variant.id);
        d.sizes = (Array.isArray(d.sizes) ? d.sizes : []).map((s) => Object.assign({}, s, { id: Number(s.id) }));
        /* the sheet lists sizes in the order the product page shows its chips */
        const order = Array.from(this.root.querySelectorAll('[data-variant-picker] [data-size-option] input[data-eu]')).map((i) => i.value);
        if (order.length) d.sizes.sort((a, b) => { const x = order.indexOf(a.size), y = order.indexOf(b.size); return (x < 0 ? 1e3 : x) - (y < 0 ? 1e3 : y); });
      } else d = null;
      this._dataNode = node;
      this._data = d;
      return d;
    }
    modeOf(d) {
      if (!d.hasSize) return 'single';
      return d.allSoldOut && !this.sizeChosen ? 'any' : 'size';
    }
    sizeOf(d, id) {
      if (!d) return null;
      return d.sizes.find((s) => same(s.id, id)) || (same(d.variant.id, id) ? d.variant : null);
    }
    contextAlerts(d, mode) {
      const ids = mode === 'any' ? d.sizes.map((s) => s.id) : [d.variant.id];
      return alertsAll().filter((a) => ids.some((id) => same(id, a.v)));
    }
    /** manage lists what the page offers (this size, or this colour's sizes) plus what the sheet has chosen */
    manageAlerts() {
      const d = this.d;
      if (!d) return [];
      const ids = (this.mode === 'any' ? d.sizes.map((s) => s.id) : [d.variant.id]).concat(this.state.chosen);
      return alertsAll().filter((a) => ids.some((id) => same(id, a.v)));
    }
    hasAlert(id, ch) { return alertsAll().some((a) => same(a.v, id) && a.ch === ch); }
    joinList(list) {
      const l = list.filter((x, i) => x && list.indexOf(x) === i).map(iso);
      if (l.length === 2 && this.i18n.and) return fill(this.i18n.and, { first: l[0], second: l[1] });
      return l.join(', ');
    }
    buttonFor() { return this.root.querySelector('[data-buy-buttons] [data-restock-open]'); }

    /** the sold-out buy state on the page, from the selected variant and what this device is on the list for */
    sync() {
      const d = this.readData();
      const buy = this.root.querySelector('[data-buy-buttons]');
      if (!d || !buy) return;
      const t = this.i18n;
      const mode = this.modeOf(d);
      buy.dataset.restockMode = mode;
      const listed = this.contextAlerts(d, mode);
      const on = listed.length > 0;
      const shorts = listed.map((a) => { const s = this.sizeOf(d, a.v); return s ? s.short : ''; });
      const fulls = listed.map((a) => { const s = this.sizeOf(d, a.v); return s ? s.size : a.size; });
      const btn = buy.querySelector('[data-restock-open]');
      const label = btn && btn.querySelector('[data-restock-label]');
      const line = buy.querySelector('[data-restock-soldout]');
      const note = buy.querySelector('[data-restock-note]');
      const onNote = buy.querySelector('[data-restock-on-note]');
      const onText = buy.querySelector('[data-restock-on-text]');
      if (line) { line.hidden = mode !== 'size'; line.textContent = fill(t.sold_out_line, { size: iso(d.variant.size) }); }
      if (btn) btn.classList.toggle('is-on-list', on);
      if (label) {
        if (on) label.textContent = mode === 'single' ? t.on_list_single : fill(t.on_list, { size: this.joinList(shorts) });
        else label.textContent = mode === 'size' ? fill(t.notify_size, { size: iso(d.variant.short) }) : t.notify_any;
      }
      if (note) {
        note.hidden = on;
        note.textContent = mode === 'size' ? fill(t.note_size, { size: iso(d.variant.size) }) : (mode === 'any' ? t.note_any : t.note_single);
      }
      if (onNote) onNote.hidden = !on;
      if (onText) onText.textContent = on ? (mode === 'single' ? t.on_note_single : fill(t.on_note, { size: this.joinList(fulls) })) : '';
    }

    /* ------------------------------------------------------------ open / views */
    openFrom(opener, opts = {}) {
      const d = this.readData();
      if (!d) return;
      this.d = d;
      this.mode = this.modeOf(d);
      /* a different variant on the page than when the sheet was last set up: start again from it; the same one
         keeps what the shopper chose in the sheet (close and reopen loses nothing) */
      if (!same(this.state.pdp, d.variant.id) || this.state.h !== d.handle) {
        this.state.h = d.handle;
        this.state.pdp = d.variant.id;
        this.state.chosen = this.mode === 'any' ? (d.sizes.length === 1 ? [d.sizes[0].id] : []) : [d.variant.id];
        this.state.replace = null;
      }
      this.state.chosen = this.state.chosen.filter((id) => this.sizeOf(d, id));
      if (this.mode !== 'any' && !this.state.chosen.length) this.state.chosen = [d.variant.id];
      if (this.mode === 'any' && this.state.chosen.length > MAX_PICK) this.state.chosen = this.state.chosen.slice(0, MAX_PICK);
      if (!this.waOn) this.state.ch = 'email';
      this.changing = false;
      const view = opts.view || (this.contextAlerts(d, this.mode).length ? 'manage' : 'form');
      this.render(view === 'manage' && !this.contextAlerts(d, this.mode).length ? 'form' : view, Object.assign({ focus: false }, opts));
      this.saveDraft();
      if (this.drawer && typeof this.drawer.open === 'function' && !this.drawer.isOpen) this.drawer.open(opener);
    }

    render(view, opts = {}) {
      const t = this.i18n;
      this.state.view = view;
      this.querySelectorAll('[data-restock-view]').forEach((v) => { v.hidden = v.dataset.restockView !== view; });
      if (this.heading) this.heading.textContent = view === 'manage' ? t.manage_title : (view === 'done' ? t.done_title : t.title);
      this.renderProduct();
      if (view === 'form') this.renderForm(opts);
      else if (view === 'manage') this.renderManage(opts);
      else if (view === 'done') this.renderDone(opts.done || {});
      if (opts.focus !== false) this.focusHeading();
    }
    focusHeading() {
      if (!this.heading || !this.drawer || !this.drawer.isOpen) return;
      try { this.heading.focus({ preventScroll: true }); } catch (e) { this.heading.focus(); }
    }

    renderProduct() {
      const d = this.d;
      if (!d) return;
      const img = this.querySelector('[data-restock-image]');
      if (img) {
        if (d.image) { if (img.getAttribute('src') !== d.image) img.src = d.image; img.alt = d.title || ''; img.hidden = false; } else { img.hidden = true; img.removeAttribute('src'); }
      }
      const title = this.querySelector('[data-restock-title]');
      if (title) title.textContent = d.title || '';
      const first = this.sizeOf(d, this.state.chosen[0]) || d.variant;
      const price = this.querySelector('[data-restock-price]');
      if (price) price.textContent = first.price || d.variant.price || '';
    }

    renderForm(opts) {
      const t = this.i18n;
      const d = this.d;
      const mode = this.mode;
      const chosen = this.state.chosen.map((id) => this.sizeOf(d, id)).filter(Boolean);
      const row = this.querySelector('[data-restock-size-row]');
      const pick = this.querySelector('[data-restock-pick]');
      const legend = this.querySelector('[data-restock-pick-legend]');
      const change = this.querySelector('[data-restock-change]');
      const pickGrid = mode === 'any' || (mode === 'size' && this.changing);
      if (row) {
        row.hidden = mode !== 'size' || this.changing;
        const txt = row.querySelector('[data-restock-size-text]');
        if (txt) txt.textContent = chosen[0] ? chosen[0].size : d.variant.size;
        if (change) { change.hidden = d.sizes.length < 2; change.setAttribute('aria-expanded', this.changing ? 'true' : 'false'); }
      }
      if (pick) {
        pick.hidden = !pickGrid;
        if (legend) legend.textContent = mode === 'any' ? t.pick_label : t.size_label;
        if (pickGrid) this.renderChips(mode !== 'any');
        this.pickHint('');
      }
      /* channel switch (only rendered when WhatsApp can deliver) */
      const seg = this.querySelector('.restock__seg');
      if (seg) {
        const idx = this.state.ch === 'email' ? 1 : 0;
        seg.dataset.active = String(idx);
        seg.querySelectorAll('[data-channel]').forEach((b) => {
          const on = b.dataset.channel === this.state.ch;
          b.setAttribute('aria-checked', on ? 'true' : 'false');
          b.tabIndex = on ? 0 : -1;
        });
      }
      if (this.waForm) this.waForm.hidden = this.state.ch !== 'whatsapp';
      if (this.emailForm) this.emailForm.hidden = this.state.ch !== 'email';
      /* the note under each submit names the size(s) */
      const sizes = chosen.map((s) => s.size).filter(Boolean);
      const noteFor = (ch) => {
        if (mode === 'single') return ch === 'email' ? t.note_email_single : t.note_whatsapp_single;
        if (mode === 'any') return ch === 'email' ? t.pick_note_email : t.pick_note_whatsapp;
        return fill(ch === 'email' ? t.note_email : t.note_whatsapp, { size: this.joinList(sizes) });
      };
      if (this.emailForm) this.emailForm.querySelector('[data-restock-channel-note]').textContent = noteFor('email');
      if (this.waForm) this.waForm.querySelector('[data-restock-channel-note]').textContent = noteFor('whatsapp');
      const email = this.querySelector('[data-restock-email]');
      if (email && !email.value && this.state.email) email.value = this.state.email;
      const phone = this.querySelector('[data-restock-phone]');
      if (phone && !phone.value && this.state.phone) phone.value = this.state.phone;
      if (!opts.keepErrors) {
        this.querySelectorAll('[data-restock-email], [data-restock-phone]').forEach((i) => this.clearFieldError(i));
        this.querySelectorAll('[data-restock-send-error]').forEach((b) => { b.hidden = true; });
      }
    }

    renderChips(single) {
      const box = this.querySelector('[data-restock-chips]');
      if (!box) return;
      const d = this.d;
      const base = this.dataset.sheet || 'restock';
      box.innerHTML = d.sizes.map((s) => {
        const id = base + '-chip-' + s.id;
        const checked = this.state.chosen.some((c) => same(c, s.id));
        const listed = this.hasAlert(s.id, this.state.ch);
        return '<input class="restock__chip-input sr-only" type="' + (single ? 'radio' : 'checkbox') + '" id="' + id + '" name="' + base + '-size" value="' + s.id + '"' + (checked ? ' checked' : '') + '>' +
          '<label class="restock__chip' + (listed ? ' is-listed' : '') + '" for="' + id + '"><span class="restock__chip-text">' + esc(s.size) + '</span>' +
          (listed ? '<span class="sr-only"> — ' + esc(this.i18n.chip_on_list) + '</span>' : '') + '</label>';
      }).join('');
      box.dataset.type = single ? 'radio' : 'checkbox';
    }
    pickHint(msg) {
      const hint = this.querySelector('[data-restock-pick-hint]');
      if (hint) { hint.textContent = msg || ''; hint.classList.toggle('is-on', !!msg); }
    }

    onChipChange(e) {
      const input = e.target;
      if (!input.matches || !input.matches('.restock__chip-input')) return;
      const id = Number(input.value);
      if (input.type === 'radio') {
        this.state.chosen = [id];
        this.changing = false;
        this.renderProduct();
        this.renderForm({});
        this.saveDraft();
        const change = this.querySelector('[data-restock-change]');
        if (change && !change.hidden) change.focus();
        return;
      }
      if (input.checked) {
        if (this.state.chosen.length >= MAX_PICK) {
          /* a third size is refused, and the shopper is told why */
          input.checked = false;
          this.pickHint(this.i18n.pick_limit);
          return;
        }
        this.state.chosen = this.state.chosen.concat(id);
      } else {
        this.state.chosen = this.state.chosen.filter((c) => !same(c, id));
      }
      this.pickHint('');
      this.renderProduct();
      this.saveDraft();
    }

    onSegKey(e) {
      const b = e.target.closest && e.target.closest('.restock__seg [data-channel]');
      if (!b) return;
      const keys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
      if (keys.indexOf(e.key) === -1) return;
      e.preventDefault();
      const btns = Array.from(this.querySelectorAll('.restock__seg [data-channel]'));
      const rtl = document.documentElement.dir === 'rtl';
      const fwd = e.key === 'ArrowDown' || (e.key === 'ArrowRight' && !rtl) || (e.key === 'ArrowLeft' && rtl);
      const next = btns[(btns.indexOf(b) + (fwd ? 1 : btns.length - 1)) % btns.length];
      this.setChannel(next.dataset.channel);
      next.focus();
    }
    setChannel(ch) {
      if (ch === 'whatsapp' && !this.waOn) ch = 'email';
      this.state.ch = ch;
      this.renderForm({});
      this.saveDraft();
    }

    onClick(e) {
      const t = e.target.closest('button, a');
      if (!t || !this.contains(t)) return;
      if (t.matches('[data-channel]')) { this.setChannel(t.dataset.channel); return; }
      if (t.matches('[data-restock-change]')) {
        this.changing = !this.changing;
        this.renderForm({ keepErrors: true });
        const checked = this.querySelector('.restock__chip-input:checked') || this.querySelector('.restock__chip-input');
        if (this.changing && checked) checked.focus();
        return;
      }
      if (t.matches('[data-restock-continue], [data-restock-done]')) { if (this.drawer) this.drawer.close(); return; }
      const action = t.dataset.action;
      if (action === 'remove') { this.removeAlert(Number(t.dataset.v), t.dataset.ch); return; }
      if (action === 'switch') {
        const v = Number(t.dataset.v);
        this.state.chosen = [v];
        this.state.replace = { v, ch: t.dataset.ch };
        this.state.ch = t.dataset.ch === 'email' ? 'whatsapp' : 'email';
        if (!this.waOn) this.state.ch = 'email';
        this.render('form', { switchFor: v });
      }
    }

    /* ------------------------------------------------------------ email: the STOQ back-in-stock app */
    validChosen() {
      const d = this.d || this.readData();
      if (!d) return [];
      if (this.mode === 'single') return [d.variant.id];
      return this.state.chosen.filter((id) => this.sizeOf(d, id)).slice(0, MAX_PICK);
    }
    async onEmailSubmit(e) {
      e.preventDefault();
      if (this._posting) return;
      const d = this.d || this.readData();
      if (!d) return;
      const chosen = this.validChosen();
      if (!chosen.length) { this.pickHint(this.i18n.pick_required); const first = this.querySelector('.restock__chip-input'); if (first) first.focus(); return; }
      const input = this.emailForm.querySelector('[data-restock-email]');
      const email = String(input.value || '').trim();
      if (!EMAIL_RE.test(email)) { this.fieldError(input, this.i18n.error_email); return; }
      input.value = email;
      this.state.email = email;
      /* already on the list by email for every chosen size: show it instead of sending again */
      const fresh = chosen.filter((id) => !this.hasAlert(id, 'email'));
      if (!fresh.length) { this.render('manage', { already: true }); return; }
      const items = fresh.map((id) => this.sizeOf(d, id)).filter(Boolean);
      const sendErr = this.emailForm.querySelector('[data-restock-send-error]');
      const failed = () => { if (sendErr) { sendErr.textContent = this.i18n.error_send; sendErr.hidden = false; } };
      if (sendErr) sendErr.hidden = true;
      this.saveDraft();
      /* a filled hidden field, or more sizes from one tab than a shopper sends: nothing goes out, nothing is promised */
      const hp = this.emailForm.querySelector('[data-restock-hp]');
      if ((hp && hp.value) || !this.underRate(items.length)) { failed(); return; }
      this.loading(true);
      const locale = this.dataset.locale || document.documentElement.lang || 'en';
      /* only what the alert needs: the address, the exact variant and the language; no marketing consent. The
         product block is what STOQ's sign-up confirmation and alert emails name the product and size with. */
      const ok = await Promise.all(items.map((s) => this.sendStoq({
        intent: { shopify_variant_id: Number(s.id), shopify_product_id: this.productId, channel: 'email', quantity: 1, source: 'api' },
        customer: { email, accepts_marketing: false, locale },
        product: { title: d.title || '', variant_title: s.title || s.size || '', vendor: d.vendor || '', variant_count: Number(d.variantCount) || 1 }
      })));
      this.loading(false);
      const done = items.filter((s, i) => ok[i]).map((s) => ({ v: s.id, size: s.size || '', short: s.short || '' }));
      if (done.length) this.remember(done, 'email', email, this.state.replace, d.handle);
      this.saveDraft();
      if (done.length === items.length) {
        this.state.replace = null;
        this.sync();
        this.render('done', { done: { items: done, ch: 'email', to: email } });
      } else {
        this.sync();
        failed();
      }
    }
    /** true once STOQ holds the request: 201 created, or 200 saying this address is already waiting for the variant */
    async sendStoq(body) {
      if (!this.shop || !this.productId) return false;
      const ctrl = typeof AbortController === 'function' ? new AbortController() : null;
      const timer = ctrl ? setTimeout(() => ctrl.abort(), SEND_MS) : 0;
      try {
        const r = await fetch(STOQ_URL, {
          method: 'POST',
          mode: 'cors',
          credentials: 'omit',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Shopify-Shop-Domain': this.shop },
          body: JSON.stringify(body),
          signal: ctrl ? ctrl.signal : undefined
        });
        let j = null;
        try { j = await r.json(); } catch (err) { j = null; }
        if (r.status === 201) return !j || !Array.isArray(j.intents) || j.intents.length > 0;
        /* STOQ answers a repeat sign-up with 200 {"errors":{"already_registered":true}}: the request is already kept */
        const errors = j && j.errors;
        return r.status === 200 && !!errors && (errors.already_registered === true || /already|duplicate/i.test(JSON.stringify(errors)));
      } catch (err) {
        return false;
      } finally {
        clearTimeout(timer);
      }
    }
    underRate(n) {
      const now = Date.now();
      const log = read('sessionStorage', RATE_KEY, []);
      const recent = (Array.isArray(log) ? log : []).filter((t) => typeof t === 'number' && now - t < RATE_MS);
      if (recent.length + n > RATE_MAX) return false;
      for (let i = 0; i < n; i++) recent.push(now);
      write('sessionStorage', RATE_KEY, recent);
      return true;
    }
    loading(on) {
      this._posting = !!on;
      clearTimeout(this._loadTimer);
      this.querySelectorAll('.restock__submit').forEach((b) => { b.classList.toggle('is-loading', !!on); if (on) b.setAttribute('aria-busy', 'true'); else b.removeAttribute('aria-busy'); });
      /* a request that never settles must not leave the button spinning */
      if (on) this._loadTimer = setTimeout(() => this.loading(false), 12000);
    }

    /* ------------------------------------------------------------ WhatsApp: the merchant's endpoint */
    async onWaSubmit(e) {
      e.preventDefault();
      if (this._posting || !this.waOn) return;
      const d = this.d || this.readData();
      if (!d) return;
      const chosen = this.validChosen();
      if (!chosen.length) { this.pickHint(this.i18n.pick_required); const first = this.querySelector('.restock__chip-input'); if (first) first.focus(); return; }
      const input = this.waForm.querySelector('[data-restock-phone]');
      const phone = normalPhone(input.value);
      if (!phone) { this.fieldError(input, this.i18n.error_phone); return; }
      this.state.phone = input.value;
      const fresh = chosen.filter((id) => !this.hasAlert(id, 'whatsapp'));
      if (!fresh.length) { this.render('manage', { already: true }); return; }
      const items = fresh.map((id) => this.sizeOf(d, id)).filter(Boolean);
      const sendErr = this.waForm.querySelector('[data-restock-send-error]');
      if (sendErr) sendErr.hidden = true;
      this.loading(true);
      const locale = this.dataset.locale || document.documentElement.lang || 'en';
      const ok = await Promise.all(items.map((s) => fetch(this.endpoint, {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ variant_id: s.id, product_handle: d.handle, size: s.size || '', phone, locale })
      }).then((r) => r.ok, () => false)));
      this.loading(false);
      const done = items.filter((s, i) => ok[i]).map((s) => ({ v: s.id, size: s.size || '', short: s.short || '' }));
      if (done.length) this.remember(done, 'whatsapp', phone, this.state.replace, d.handle);
      this.saveDraft();
      if (done.length === items.length) {
        this.state.replace = null;
        this.sync();
        this.render('done', { done: { items: done, ch: 'whatsapp', to: phone } });
      } else if (sendErr) {
        this.sync();
        sendErr.textContent = this.i18n.error_send;
        sendErr.hidden = false;
      }
    }

    /* ------------------------------------------------------------ device list */
    remember(items, ch, to, replace, handle) {
      let list = alertsAll();
      items.forEach((it) => {
        list = list.filter((a) => !(same(a.v, it.v) && (a.ch === ch || (replace && same(replace.v, it.v) && a.ch === replace.ch))));
        list.push({ v: Number(it.v), p: handle, size: it.size || '', ch, to: mask(ch, to), t: Date.now() });
      });
      saveAlerts(list);
    }
    removeAlert(v, ch) {
      const all = alertsAll();
      const gone = all.filter((a) => same(a.v, v) && a.ch === ch);
      saveAlerts(all.filter((a) => !(same(a.v, v) && a.ch === ch)));
      this.sync();
      const d = this.d;
      const left = d ? this.manageAlerts() : [];
      if (left.length) this.render('manage', {});
      else if (this.drawer) this.drawer.close();
      const size = gone[0] && gone[0].size;
      this.toast(this.i18n.toast_removed, {
        undo: () => {
          saveAlerts(alertsAll().filter((a) => !gone.some((g) => same(g.v, a.v) && g.ch === a.ch)).concat(gone));
          this.sync();
          if (this.drawer && this.drawer.isOpen && this.state.view === 'manage') this.render('manage', {});
          this.toast(size ? fill(this.i18n.toast_on_list, { size: iso(size) }) : this.i18n.toast_on_list_single, { check: true });
        }
      });
    }

    renderManage(opts) {
      const t = this.i18n;
      const d = this.d;
      const listed = this.manageAlerts();
      const already = this.querySelector('[data-restock-already]');
      if (already) already.hidden = !opts.already;
      const ul = this.querySelector('[data-restock-alerts]');
      if (ul) {
        ul.innerHTML = listed.map((a) => {
          const s = this.sizeOf(d, a.v);
          const size = (s && s.size) || a.size || '';
          const chName = a.ch === 'whatsapp' ? t.channel_whatsapp : t.channel_email;
          const pill = size ? fill(t.pill, { size, channel: chName, address: a.to }) : fill(t.pill_single, { channel: chName, address: a.to });
          const canSwitch = a.ch === 'whatsapp' || this.waOn;
          const sw = canSwitch ? '<button type="button" class="restock__link" data-action="switch" data-v="' + esc(a.v) + '" data-ch="' + esc(a.ch) + '" data-no-transition>' + esc(a.ch === 'whatsapp' ? t.switch_email : t.switch_whatsapp) + '</button>' : '';
          return '<li class="restock__alert">' +
            '<span class="restock__pill">' + (a.ch === 'whatsapp' ? this.iconHtml('whatsapp') : this.iconHtml('mail')) + '<span>' + esc(pill) + '</span></span>' +
            '<span class="restock__alert-actions">' + sw +
              '<button type="button" class="restock__link restock__link--remove" data-action="remove" data-v="' + esc(a.v) + '" data-ch="' + esc(a.ch) + '" aria-label="' + esc(fill(t.remove_aria, { size: size || (d && d.title) || '' })) + '" data-no-transition>' + esc(t.remove) + '</button>' +
            '</span></li>';
        }).join('');
      }
      const fine = this.querySelector('[data-restock-fine]');
      if (fine) {
        const chans = listed.map((a) => a.ch);
        fine.textContent = [chans.indexOf('email') !== -1 ? t.manage_note_email : '', chans.indexOf('whatsapp') !== -1 ? t.manage_note_whatsapp : ''].filter(Boolean).join(' ');
        fine.hidden = !fine.textContent;
      }
    }
    iconHtml(name) {
      const src = this.querySelector('svg.icon--' + name);
      return src ? src.outerHTML : '';
    }

    renderDone(done) {
      const t = this.i18n;
      const items = Array.isArray(done.items) ? done.items : [];
      const sizes = items.map((i) => i.size).filter(Boolean);
      const wa = done.ch === 'whatsapp';
      const address = wa ? prettyPhone(done.to) : (done.to || '');
      const line = this.querySelector('[data-restock-done-line]');
      const body = this.querySelector('[data-restock-done-body]');
      if (line) line.textContent = sizes.length ? fill(t.done_line, { size: this.joinList(sizes) }) : t.done_line_single;
      let tpl;
      if (!sizes.length) tpl = wa ? t.done_whatsapp_single : t.done_email_single;
      else if (sizes.length > 1) tpl = wa ? t.done_whatsapp_many : t.done_email_many;
      else tpl = wa ? t.done_whatsapp : t.done_email;
      if (body) body.textContent = fill(tpl, { address: iso(address), size: iso(sizes[0] || '') });
      this._doneSizes = sizes;
    }

    onClosed() {
      const wasDone = this.state.view === 'done';
      this.changing = false;
      if (wasDone) {
        const sizes = this._doneSizes || [];
        this.toast(sizes.length ? fill(this.i18n.toast_on_list, { size: this.joinList(sizes) }) : this.i18n.toast_on_list_single, { check: true });
        this.state.view = 'form';
      }
    }

    /* ------------------------------------------------------------ deep links */
    /** quick add links here with #notify (any size) or #notify-size (the size it showed) */
    handleHash() {
      const h = window.location.hash;
      if (h !== '#notify' && h !== '#notify-size') return false;
      const d = this.readData();
      try { window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search); } catch (e) { /* keep the hash */ }
      if (!d) return false;
      if (h === '#notify-size') this.sizeChosen = true;
      this.sync();
      setTimeout(() => this.openFrom(this.buttonFor() || undefined), 60);
      return true;
    }

    /* ------------------------------------------------------------ field errors, draft, toast */
    fieldError(input, msg) {
      const field = input.closest('.field');
      const box = field && field.querySelector('[data-restock-field-error]');
      if (box) { box.querySelector('span').textContent = msg; box.hidden = false; }
      if (field) field.classList.add('is-invalid');
      input.setAttribute('aria-invalid', 'true');
      const note = input.getAttribute('aria-describedby') || '';
      if (box && note.indexOf(box.id) === -1) input.setAttribute('aria-describedby', (box.id + ' ' + note).trim());
      input.focus();
    }
    clearFieldError(input) {
      const field = input.closest('.field');
      const box = field && field.querySelector('[data-restock-field-error]');
      if (box) box.hidden = true;
      if (field) field.classList.remove('is-invalid');
      input.removeAttribute('aria-invalid');
      if (box) input.setAttribute('aria-describedby', (input.getAttribute('aria-describedby') || '').split(' ').filter((x) => x && x !== box.id).join(' '));
    }
    saveDraft() {
      const s = this.state;
      write('sessionStorage', DRAFT_KEY, { h: s.h, pdp: s.pdp, chosen: s.chosen, ch: s.ch, email: s.email, phone: s.phone });
    }
    restoreDraft() {
      const d = this.readData();
      const draft = read('sessionStorage', DRAFT_KEY, null);
      if (!draft || !d || draft.h !== d.handle) return;
      this.state.h = draft.h;
      this.state.pdp = draft.pdp;
      this.state.chosen = Array.isArray(draft.chosen) ? draft.chosen.map(Number) : [];
      this.state.ch = draft.ch === 'whatsapp' && this.waOn ? 'whatsapp' : 'email';
      this.state.email = draft.email || '';
      this.state.phone = draft.phone || '';
      const email = this.querySelector('[data-restock-email]');
      if (email && !email.value && this.state.email) email.value = this.state.email;
      const phone = this.querySelector('[data-restock-phone]');
      if (phone && !phone.value && this.state.phone) phone.value = this.state.phone;
    }

    /** C5 toasts. theme.toast prints text only, so the one with Undo is this element (same .sf-toast look). */
    toast(message, opts = {}) {
      let el = document.getElementById('restock-toast');
      if (!el) {
        el = document.createElement('div');
        el.id = 'restock-toast';
        el.className = 'sf-toast restock-toast';
        el.setAttribute('role', 'status');
        el.setAttribute('aria-live', 'polite');
        el.hidden = true;
        document.body.appendChild(el);
        el.addEventListener('mouseenter', () => clearTimeout(el._timer));
        el.addEventListener('focusin', () => clearTimeout(el._timer));
        el.addEventListener('mouseleave', () => el._arm && el._arm());
        el.addEventListener('focusout', (e) => { if (!el.contains(e.relatedTarget) && el._arm) el._arm(); });
      }
      clearTimeout(el._timer);
      clearTimeout(el._hideTimer);
      const check = opts.check ? this.iconHtml('check') : '';
      el.innerHTML = check + '<span>' + esc(message) + '</span>' + (opts.undo
        ? ' <span class="restock-toast__sep" aria-hidden="true">·</span> <button type="button" class="restock-toast__undo" data-no-transition>' + esc(this.i18n.undo || 'Undo') + '</button>'
        : '');
      const hide = () => {
        el.classList.remove('is-open');
        el._hideTimer = setTimeout(() => { if (!el.classList.contains('is-open')) el.hidden = true; }, 260);
      };
      el._arm = () => { clearTimeout(el._timer); el._timer = setTimeout(hide, opts.undo ? UNDO_MS : 2600); };
      const undo = el.querySelector('.restock-toast__undo');
      if (undo) undo.addEventListener('click', () => { clearTimeout(el._timer); hide(); opts.undo(); }, { once: true });
      el.hidden = false;
      requestAnimationFrame(() => requestAnimationFrame(() => el.classList.add('is-open')));
      el._arm();
    }
  }

  customElements.define('sf-restock-sheet', SFRestockSheet);
})();
